import { Injectable } from '@angular/core';
import { Issue, Resolution } from './issue.dto';
import { map, Observable } from 'rxjs';
import { ObservableSyncService } from '../sync/observable-sync.service';
import { ChangeTyped, ISSUES_STORE, mapSnapshotToDtos, QueryService, SearchResult } from '@damofire/data-commons';
import { ObservableIssueService } from './issue-observable.service';
import { TypedDesQuery } from '../des.dto';
import { ObservableNoteService } from '../note/note-observable.service';

@Injectable()
export class IdbObservableIssueService extends ObservableIssueService {
  constructor(
    protected readonly observableSyncService: ObservableSyncService<Issue>,
    protected readonly observableNoteService: ObservableNoteService,
  ) {
    super(observableNoteService);
  }

  protected observeById(division: string, id: string): Observable<ChangeTyped<Issue>> {
    return this.observableSyncService.toObservableById(ISSUES_STORE, division, id);
  }

  observeByAssets(divisionId: string, ids: number[]): Observable<ChangeTyped<Issue>[]> {
    return this.observableSyncService.toObservable(ISSUES_STORE, divisionId, (issues) => this.filterByAssets(issues, ids));
  }

  protected observeOpen(id: string, section?: string): Observable<ChangeTyped<Issue>[]> {
    return this.observableSyncService.toObservable(ISSUES_STORE, id, (issues) => this.filterByResolution(issues, Resolution.OPEN));
  }

  protected observeByInspection(divisionId: string, id: string): Observable<ChangeTyped<Issue>[]> {
    return this.observableSyncService.toObservable(ISSUES_STORE, divisionId, (issues) => this.filterByInspection(issues, +id));
  }

  observeByAsset(divisionId: string, assetId: number, inspectionId?: string): Observable<ChangeTyped<Issue>[]> {
    return this.observableSyncService.toObservableDivisionIndex(ISSUES_STORE, 'asset', assetId, divisionId, (issues) => this.filterByInspection(issues, +inspectionId));
  }

  observeOpenByAsset(divisionId: string, assetId: number): Observable<ChangeTyped<Issue>[]> {
    return this.observableSyncService.toObservableDivisionIndex(ISSUES_STORE, 'asset', assetId, divisionId, (issues) =>
      this.filterByAssetAndResolution(issues, assetId, Resolution.OPEN),
    );
  }

  noteableSearch(id: string, sQuery: TypedDesQuery): Observable<SearchResult<Issue>> {
    let previousResults: Issue[] = [];
    return this.observableSyncService.toObservable(ISSUES_STORE, id).pipe(
      map((changes) => {
        mapSnapshotToDtos(previousResults, changes);
        const searchResults = QueryService.searchCollection<Issue>(previousResults, sQuery, this.SEARCH_FIELDS);
        previousResults = searchResults.result;
        return searchResults;
      }),
    );
  }

  private filterByAssets(issues: ChangeTyped<Issue>[], assetIds: number[]): ChangeTyped<Issue>[] {
    return issues.filter((i) => assetIds.includes(+i.asset._id));
  }

  private filterByResolution(issues: ChangeTyped<Issue>[], resolution: Resolution): ChangeTyped<Issue>[] {
    return issues.filter((i) => i.resolution === resolution);
  }

  private filterByAssetAndResolution(issues: ChangeTyped<Issue>[], assetId: number, resolution: Resolution): ChangeTyped<Issue>[] {
    return issues.filter((i) => i.asset._id === assetId && i.resolution === resolution);
  }

  private filterByInspection(issues: ChangeTyped<Issue>[], inspectionId?: number): ChangeTyped<Issue>[] {
    return !inspectionId ? issues : issues.filter((i) => i.inspection?._id === inspectionId);
  }
}
